import React from 'react';
import Navbar from '../../components/website/Navbar';
import Footer from '../../components/website/Footer';

const Section = ({ n, title, children }) => (
  <section className="mt-8">
    <h2 className="font-heading font-bold text-xl sm:text-2xl text-rozops-navy tracking-tight">
      {n}. {title}
    </h2>
    <div className="mt-3 text-rozops-text-muted text-[15px] leading-relaxed space-y-2">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="mt-2 space-y-1.5">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-2">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-rozops-blue flex-shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function Privacy() {
  return (
    <div className="bg-rozops-bg min-h-screen">
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="privacy-page">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-rozops-blue-light text-rozops-blue text-xs font-semibold px-3 py-1">Legal</div>
        <h1 className="mt-4 font-heading font-extrabold text-4xl sm:text-5xl text-rozops-navy tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-sm text-rozops-text-muted">Effective Date: 14th July, 2026.</p>

        <div className="mt-8 bg-white rounded-2xl border border-gray-100 shadow-soft p-6 sm:p-8">
          <p className="text-rozops-text-muted text-[15px] leading-relaxed">Welcome to RozOps.</p>
          <p className="mt-3 text-rozops-text-muted text-[15px] leading-relaxed">
            RozOps (<span className="font-semibold">"we", "our", "us"</span>) is operated by
            <span className="font-semibold text-rozops-text"> Nexwis Innovations Private Limited</span>. We are committed to protecting your privacy and handling your information responsibly.
          </p>

          <Section n={1} title="Information We Collect">
            <p>We may collect the following information:</p>
            <div className="font-semibold text-rozops-text mt-3">Personal Information</div>
            <Bullets items={[
              'Full Name',
              'Mobile Number',
              'Email Address',
              'Business Name',
              'Business Address',
              'Customer Information entered by merchants',
              'Delivery Addresses',
              'Profile Photos (if uploaded)',
            ]} />
            <div className="font-semibold text-rozops-text mt-4">Business Information</div>
            <Bullets items={[
              'Products',
              'Pricing',
              'Customer Lists',
              'Subscription Details',
              'Delivery Records',
              'Billing Information',
              'Payment Records',
              'Reports',
            ]} />
            <div className="font-semibold text-rozops-text mt-4">Device Information</div>
            <Bullets items={[
              'Browser type',
              'Device information',
              'IP address',
              'Operating system',
              'Login activity',
              'Cookies and analytics information',
            ]} />
          </Section>

          <Section n={2} title="How We Use Your Information">
            <p>We use your information to:</p>
            <Bullets items={[
              'Create and manage your account',
              'Provide RozOps services',
              'Generate invoices and reports',
              'Manage subscriptions and deliveries',
              'Send notifications and reminders',
              'Improve platform performance',
              'Provide customer support',
              'Detect fraud and misuse',
              'Comply with legal obligations',
            ]} />
          </Section>

          <Section n={3} title="Customer Data">
            <p>Merchants using RozOps are responsible for ensuring they have the necessary rights and permissions to collect and upload customer information into the platform.</p>
            <p>RozOps acts as a technology service provider and processes customer data only for providing the requested services.</p>
          </Section>

          <Section n={4} title="Data Security">
            <p>We implement reasonable technical and organizational security measures to protect your information from unauthorized access, alteration, disclosure, or destruction.</p>
            <p>However, no online service can guarantee absolute security.</p>
          </Section>

          <Section n={5} title="Data Sharing">
            <p>We do not sell your personal information.</p>
            <p>We may share information only:</p>
            <Bullets items={[
              'With trusted service providers assisting in platform operations',
              'When required by law',
              'To protect our legal rights',
              'During mergers, acquisitions, or business restructuring',
            ]} />
          </Section>

          <Section n={6} title="Cookies">
            <p>RozOps may use cookies and similar technologies to improve user experience, remember preferences, and analyze website usage.</p>
            <p>You may disable cookies through your browser settings, although some features may not function properly.</p>
          </Section>

          <Section n={7} title="Data Retention">
            <p>We retain information only as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce agreements.</p>
          </Section>

          <Section n={8} title="Your Rights">
            <p>Subject to applicable laws, you may request to:</p>
            <Bullets items={[
              'Access your information',
              'Correct inaccurate information',
              'Update account details',
              'Delete your account',
              'Export your data where technically feasible',
            ]} />
          </Section>

          <Section n={9} title="Third-Party Services">
            <p>RozOps may integrate with third-party services such as payment gateways, messaging providers, analytics providers, and cloud infrastructure providers. Their use of your information is governed by their respective privacy policies.</p>
          </Section>

          <Section n={10} title="Children's Privacy">
            <p>RozOps is intended for business users and is not directed toward children under 18 years of age.</p>
          </Section>

          <Section n={11} title="Changes to this Policy">
            <p>We may update this Privacy Policy from time to time. Updated versions will be published on this page with the revised effective date.</p>
          </Section>

          <Section n={12} title="Contact Us">
            <div className="mt-2 grid gap-1.5 text-[15px]">
              <div><span className="font-semibold text-rozops-text">Brand:</span> RozOps</div>
              <div><span className="font-semibold text-rozops-text">Company:</span> Nexwis Innovations Private Limited</div>
              <div><span className="font-semibold text-rozops-text">Contact Person:</span> Kranthi Kumar Kukkala</div>
              <div><span className="font-semibold text-rozops-text">Mobile:</span> <a href="tel:+917672002525" className="text-rozops-blue">+91 7672002525</a></div>
              <div><span className="font-semibold text-rozops-text">Email:</span> <a href="mailto:hello@rozops.com" className="text-rozops-blue">hello@rozops.com</a></div>
              <div className="mt-3">
                <span className="font-semibold text-rozops-text">Registered Address:</span>
                <div className="text-rozops-text-muted mt-1 leading-relaxed">
                  Nexwis Innovations Private Limited<br />
                  T-HUB FOUNDATION, SY NO. 83/1,<br />
                  Raidurg, Hyderabad, Telangana, India – 500081
                </div>
              </div>
            </div>
          </Section>
        </div>
      </article>
      <Footer />
    </div>
  );
}
